import type {
	ProductionQuestion,
	ProductionQuestionAttachment,
	ProductionQuestionListQuery,
	ProductionQuestionListResponse,
	ProductionQuestionStatus,
	ProductionQuestionCategory,
	ProductionQuestionResolutionCategory,
	ProductionQuestionResolutionSource,
} from '../../interfaces/ProductionQuestion.interface';
import { api } from '../slices/api';

export interface CreateProductionQuestionArgs {
	lineItemIds: string[];
	category: ProductionQuestionCategory;
	description?: string;
	status?: ProductionQuestionStatus;
	resolution_category?: ProductionQuestionResolutionCategory;
	resolution_source?: ProductionQuestionResolutionSource;
	resolution_description?: string;
	drawingFiles?: File[];
	modelFiles?: File[];
}

export interface UpdateProductionQuestionArgs {
	questionId: string;
	/** Uses the global endpoint when omitted. */
	lineItemId?: string;
	lineItemIds?: string[];
	category?: ProductionQuestionCategory;
	description?: string;
	status?: ProductionQuestionStatus;
	resolution_category?: ProductionQuestionResolutionCategory;
	resolution_source?: ProductionQuestionResolutionSource;
	resolution_description?: string;
}

const appendCreateFields = (
	body: FormData,
	{
		category,
		description,
		status,
		resolution_category,
		resolution_source,
		resolution_description,
		drawingFiles = [],
		modelFiles = [],
	}: Omit<CreateProductionQuestionArgs, 'lineItemIds'>
) => {
	body.append('category', category);
	if (description) body.append('description', description);
	if (status) body.append('status', status);
	if (resolution_category) body.append('resolution_category', resolution_category);
	if (resolution_source) body.append('resolution_source', resolution_source);
	if (resolution_description) {
		body.append('resolution_description', resolution_description);
	}
	drawingFiles.forEach((file) => body.append('drawing_files', file));
	modelFiles.forEach((file) => body.append('model_files', file));
};

export const productionQuestionsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getProductionQuestions: builder.query<
			ProductionQuestionListResponse,
			ProductionQuestionListQuery | void
		>({
			query: (params) => ({
				url: 'orders/production-questions/',
				params: params ?? {},
			}),
			providesTags: (result) =>
				result
					? [
							...result.results.map(({ id }) => ({
								type: 'ProductionQuestion' as const,
								id,
							})),
							{ type: 'ProductionQuestion', id: 'LIST' },
						]
					: [{ type: 'ProductionQuestion', id: 'LIST' }],
		}),
		getOrderLineItemProductionQuestions: builder.query<
			ProductionQuestion[],
			string
		>({
			query: (lineItemId) =>
				`orders/line-items/${lineItemId}/production-questions/`,
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: 'ProductionQuestion' as const,
								id,
							})),
							{ type: 'ProductionQuestion', id: 'OLI_LIST' },
						]
					: [{ type: 'ProductionQuestion', id: 'OLI_LIST' }],
		}),
		createProductionQuestion: builder.mutation<
			ProductionQuestion,
			CreateProductionQuestionArgs
		>({
			query: ({ lineItemIds, ...fields }) => {
				const body = new FormData();
				lineItemIds.forEach((id) => body.append('order_line_item_ids', id));
				appendCreateFields(body, fields);

				const useNestedEndpoint = lineItemIds.length === 1;
				return {
					url: useNestedEndpoint
						? `orders/line-items/${lineItemIds[0]}/production-questions/`
						: 'orders/production-questions/',
					method: 'POST',
					body,
				};
			},
			invalidatesTags: [
				{ type: 'ProductionQuestion', id: 'LIST' },
				{ type: 'ProductionQuestion', id: 'OLI_LIST' },
				'OrderLineItem',
			],
		}),
		updateProductionQuestion: builder.mutation<
			ProductionQuestion,
			UpdateProductionQuestionArgs
		>({
			query: ({ lineItemId, questionId, lineItemIds, ...values }) => ({
				url: lineItemId
					? `orders/line-items/${lineItemId}/production-questions/${questionId}/`
					: `orders/production-questions/${questionId}/`,
				method: 'PATCH',
				body: lineItemIds?.length
					? { ...values, order_line_item_ids: lineItemIds }
					: values,
			}),
			invalidatesTags: (_result, _error, { questionId }) => [
				{ type: 'ProductionQuestion', id: questionId },
				{ type: 'ProductionQuestion', id: 'LIST' },
				{ type: 'ProductionQuestion', id: 'OLI_LIST' },
				'OrderLineItem',
			],
		}),
		addProductionQuestionAttachments: builder.mutation<
			ProductionQuestionAttachment[],
			{
				lineItemId: string;
				questionId: string;
				kind: 'drawing' | 'model';
				files: File[];
			}
		>({
			query: ({ lineItemId, questionId, kind, files }) => {
				const body = new FormData();
				body.append('kind', kind);
				files.forEach((file) => body.append('files', file));
				return {
					url: `orders/line-items/${lineItemId}/production-questions/${questionId}/attachments/`,
					method: 'POST',
					body,
				};
			},
			invalidatesTags: (_result, _error, { questionId }) => [
				{ type: 'ProductionQuestion', id: questionId },
				{ type: 'ProductionQuestion', id: 'LIST' },
				{ type: 'ProductionQuestion', id: 'OLI_LIST' },
			],
		}),
		deleteProductionQuestionAttachment: builder.mutation<
			void,
			{
				lineItemId: string;
				questionId: string;
				attachmentId: string;
			}
		>({
			query: ({ lineItemId, questionId, attachmentId }) => ({
				url: `orders/line-items/${lineItemId}/production-questions/${questionId}/attachments/${attachmentId}/`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _error, { questionId }) => [
				{ type: 'ProductionQuestion', id: questionId },
				{ type: 'ProductionQuestion', id: 'LIST' },
				{ type: 'ProductionQuestion', id: 'OLI_LIST' },
			],
		}),
	}),
});

export const {
	useGetProductionQuestionsQuery,
	useGetOrderLineItemProductionQuestionsQuery,
	useCreateProductionQuestionMutation,
	useUpdateProductionQuestionMutation,
	useAddProductionQuestionAttachmentsMutation,
	useDeleteProductionQuestionAttachmentMutation,
} = productionQuestionsApi;
